// ==================== STATS CARDS COMPONENT ====================

import { motion } from 'framer-motion';
import { 
  ArrowUpRight, 
  ArrowDownRight, 
  Users, 
  MessageSquare, 
  Send, 
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { cn } from '../../../utils/helpers';
import { staggerContainer, staggerItem } from '../../../utils/animations';
import type { DashboardOverviewStats } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'positive' | 'negative' | 'neutral';
  changeLabel?: string;
  icon: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  isLoading?: boolean;
}

// ==================== SINGLE STAT CARD ====================

export const StatCard = ({
  title,
  value,
  change,
  changeType = 'neutral',
  changeLabel,
  icon: Icon,
  iconColor = 'text-(--color-signal-orange)',
  iconBgColor = 'bg-(--color-orange-glow)',
  isLoading = false,
}: StatCardProps) => {
  const formatValue = (val: string | number): string => {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-start justify-between animate-pulse">
          <div className="space-y-2">
            <div className="h-4 w-24 bg-(--color-border-gray) rounded" />
            <div className="h-8 w-16 bg-(--color-border-gray) rounded" />
          </div>
          <div className={cn('w-12 h-12 rounded-xl', iconBgColor)} />
        </div>
      </Card>
    );
  }

  return (
    <Card className="hover:border-(--color-signal-orange)/30 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-(--color-muted-gray) mb-1">{title}</p>
          <p className="text-2xl lg:text-3xl font-bold text-white font-heading">
            {formatValue(value)}
          </p>
          {(change !== undefined || changeLabel) && (
            <div className="flex items-center gap-1 mt-2">
              {changeType === 'positive' && (
                <ArrowUpRight size={14} className="text-(--color-success)" />
              )}
              {changeType === 'negative' && (
                <ArrowDownRight size={14} className="text-(--color-error)" />
              )}
              <span
                className={cn(
                  'text-xs font-medium',
                  changeType === 'positive' && 'text-(--color-success)',
                  changeType === 'negative' && 'text-(--color-error)',
                  changeType === 'neutral' && 'text-(--color-muted-gray)'
                )}
              >
                {changeLabel || (change !== undefined ? `${change > 0 ? '+' : ''}${change}` : '')}
              </span>
            </div>
          )}
        </div>
        <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', iconBgColor)}>
          <Icon className={iconColor} size={24} />
        </div>
      </div>
    </Card>
  );
};

// ==================== STATS GRID ====================

interface StatsGridProps {
  stats: DashboardOverviewStats | null;
  isLoading?: boolean;
}

export const StatsGrid = ({ stats, isLoading = false }: StatsGridProps) => {
  const statCards: Array<{
    title: string;
    value: number;
    change?: number;
    changeType: 'positive' | 'negative' | 'neutral';
    changeLabel?: string;
    iconColor: string;
    iconBgColor: string;
  }> = [
    {
      title: 'Total Contacts',
      value: stats?.contacts.total || 0,
      change: stats?.contacts.change,
      changeType: stats?.contacts.changeType || 'neutral',
      changeLabel: stats?.contacts.change 
        ? `${stats.contacts.change > 0 ? '+' : ''}${stats.contacts.change} this period`
        : undefined,
      iconColor: 'text-(--color-signal-orange)',
      iconBgColor: 'bg-(--color-orange-glow)',
    },
    {
      title: 'WhatsApp Sent',
      value: stats?.messages.sent || 0,
      changeLabel: stats ? `${stats.messages.deliveryRate}% delivered` : undefined,
      changeType: (stats?.messages.deliveryRate || 0) >= 90 ? 'positive' : 
                  (stats?.messages.deliveryRate || 0) >= 70 ? 'neutral' : 'negative',
      iconColor: 'text-green-400',
      iconBgColor: 'bg-green-500/10',
    },
    {
      title: 'SMS Sent',
      value: stats?.smsMessages?.sent || 0,
      changeLabel: stats?.smsMessages ? `${stats.smsMessages.deliveryRate}% delivered` : undefined,
      changeType: (stats?.smsMessages?.deliveryRate || 0) >= 90 ? 'positive' : 
                  (stats?.smsMessages?.deliveryRate || 0) >= 70 ? 'neutral' : 'negative',
      iconColor: 'text-purple-400',
      iconBgColor: 'bg-purple-500/10',
    },
    {
      title: 'Active Campaigns',
      value: stats?.campaigns.running || 0,
      changeLabel: stats ? `${stats.campaigns.scheduled} scheduled` : undefined,
      changeType: 'neutral',
      iconColor: 'text-blue-400',
      iconBgColor: 'bg-blue-500/10',
    },
    {
      title: 'WhatsApp Templates',
      value: stats?.templates.approved || 0,
      changeLabel: stats?.templates.pending 
        ? `${stats.templates.pending} pending` 
        : undefined,
      changeType: 'neutral',
      iconColor: 'text-green-400',
      iconBgColor: 'bg-green-500/10',
    },
    {
      title: 'SMS Templates',
      value: stats?.smsTemplates?.active || 0,
      changeLabel: stats?.smsTemplates 
        ? `${stats.smsTemplates.total} total` 
        : undefined,
      changeType: 'neutral',
      iconColor: 'text-purple-400',
      iconBgColor: 'bg-purple-500/10',
    },
  ];

  const icons: LucideIcon[] = [Users, MessageSquare, MessageSquare, Send, FileText, FileText];

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 lg:gap-4"
    >
      {statCards.map((stat, index) => (
        <motion.div key={stat.title} variants={staggerItem}>
          <StatCard
            {...stat}
            icon={icons[index]}
            isLoading={isLoading}
          />
        </motion.div>
      ))}
    </motion.div>
  );
};

export default StatsGrid;
